import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { AgentBuilderService } from '../services/agent-builder.service';
import { urlOrDefaultValidator } from '../../../../../factory/custom-validators';
import { CheckableSettings, TreeViewModule } from '@progress/kendo-angular-treeview';
import { CUI_PARENT_CODE } from '../../../../../constants/sensitivity.constants';
import { toUniqueExpandedLabels } from '../../../../../utilities/sensitivity-label.utils';

import { ISensitivityLabelExpanded } from '../../../../../interfaces/i-sensitivity-label';
import { MatIconModule } from '@angular/material/icon';
import { ProfileService } from '../../../../../services/profile.service';
import { AgentService } from '../../../services/agent.service';
import { KeywordsControllerComponent } from '../../../../../shared/keywords-controller/keywords-controller.component';
import { KeywordService } from '../../../../../services/keyword.service';
import { SensitivityLabelService } from '../../../../../services/sensitivity-label.service';

@Component({
  selector: 'agent-details',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TreeViewModule,
    KeywordsControllerComponent
  ],
  templateUrl: './agent-details.component.html',
  styleUrl: './agent-details.component.scss'
})
export class AgentDetailsComponent {
  agentForm!: FormGroup;

  /** Emits the latest value of the name field on every change. */
  readonly nameChange = output<string>();

  // Kendo UI TreeView
  expandedKeys: string[] = [CUI_PARENT_CODE];
  readonly checkedKeys = signal<string[]>([]);
  get sensitivityLabelData(): ISensitivityLabelExpanded[] {
    return this.#sensitivityLabelService.labels();
  }
  checkableSettings: CheckableSettings = {
    checkChildren: true,
    checkDisabledChildren: true, // Check all children
    checkParents: true,
    enabled: true,  // Enable checkable mode
    mode: 'multiple', // Multiple selection mode
    checkOnClick: true, // Check all parents when children are checked
    uncheckCollapsedChildren: false // Uncheck all children when parent is unchecked,
  };

  // Hardcoded keywords (kept for reference/fallback)
  hardcodedKeywordStrings = [ 'Proposal', 'Contract', 'Invoice', 'Report', 'Meeting Notes', 'Presentation', 'Budget', 'Plan', 'Summary', 'Analysis' ];
  // T - 2556 + 5430
  // Disable CUI items (parent + children) always; also disable all items when the user only has viewer-level permissions and is editing.
  isSensitivityLabelDisabled = (item: object): boolean => {
    const label = item as ISensitivityLabelExpanded;
    if (label.parent === CUI_PARENT_CODE) {
      return true;
    }
    const config = this.#profileService.userProfile()?.config;
    return config?.canManageAgents === false && config?.canCreateAgents === false && this.#agentService.isEditAgent();
  }
  // END Kendo UI TreeView

  #formBuilder = inject(FormBuilder);
  #agentBuilderService = inject(AgentBuilderService);
  #profileService = inject(ProfileService);
  #agentService = inject(AgentService);
  #keywordService = inject(KeywordService);
  #destroyRef = inject(DestroyRef);
  #sensitivityLabelService = inject(SensitivityLabelService);

  /** Whether the sensitivity-label picker is shown; hidden when the feature is disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  // Merge API keywords with legacy hardcoded strings
  allKeywords = computed(() =>
    this.#keywordService.mergeWithLegacyStrings(this.hardcodedKeywordStrings)
  );
  isLoadingKeywords = this.#keywordService.isLoading;

  constructor() {
    // Load keywords from API
    this.#keywordService.loadKeywords();
    effect(() => {
      const agent = this.#agentBuilderService.agent();
      if (!agent) return;

      if (agent.details.sensitivityLabels?.length) {
        // toUniqueExpandedLabels resolves legacy ids and de-duplicates before mapping to GUIDs.
        this.checkedKeys.set(toUniqueExpandedLabels(agent.details.sensitivityLabels).map(l => l.id));
      }

      this.agentForm = this.#formBuilder.group({
        name: new FormControl(agent.details.name, [Validators.required]),
        description: new FormControl(agent.details.description, [Validators.required]),
        keywords: new FormControl(agent.details.keywords),
        supportUrl: new FormControl(agent.details.supportUrl, [urlOrDefaultValidator]),
        sensitivityLabels: new FormControl(untracked(() => this.checkedKeys()), [this.sensitivityLabelValidator.bind(this)]),
      });

      this.agentForm.get('name')!.valueChanges.pipe(
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe(name => this.nameChange.emit(name ?? ''));
    });
  }

  /**
   * Decides whether the checkbox should be displayed for the item.
   * The CUI parent node has no checkbox; CUI children show a disabled checkbox.
   */
  hasCheckbox = (item: object, _index: string): boolean => {
    const label = item as ISensitivityLabelExpanded;
    return label.id !== CUI_PARENT_CODE;
  };

  /**
   * Fired when the list of checked keys changes (including label clicks when checkOnClick is enabled).
   * Keeps the form control in sync with the TreeView checked state.
   */
  onCheckedKeysChange(checkedKeys: string[]): void {
    this.checkedKeys.set(checkedKeys);
    this.agentForm.get('sensitivityLabels')?.setValue([...checkedKeys]);
    this.agentForm.get('sensitivityLabels')?.markAsTouched();
    this.agentForm.get('sensitivityLabels')?.updateValueAndValidity();
  }

  sensitivityLabelValidator(control: AbstractControl): ValidationErrors | null {
    // When the sensitivity-label feature is disabled the picker is hidden, so it is not required.
    if (!this.#sensitivityLabelService.enabled()) {
      return null;
    }
    if (Array.isArray(control.value) && control.value.length === 0) {
      return { sensitivityLabelInvalid: true };
    }
    return null;
  }

  /**
   * Marks all controls in the agent form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the AgentBuilderComponent.
   */
  markFormAsTouched(): void {
    this.agentForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.agentForm) {
      return false;
    }

    // Mark all fields as touched first to show validation errors
    this.agentForm.markAllAsTouched();

    // Reset form errors
    this.agentForm.get('name')?.setErrors(null);
    this.agentForm.get('description')?.setErrors(null);
    this.agentForm.get('sensitivityLabels')?.setErrors(null);

    // Validate required fields
    if (this.agentForm.get('name')?.value === '') {
      this.agentForm.get('name')?.setErrors({ required: true });
      this.agentForm.get('name')?.markAsTouched();
    }
    if (this.agentForm.get('description')?.value === '') {
      this.agentForm.get('description')?.setErrors({ required: true });
      this.agentForm.get('description')?.markAsTouched();
    }

    // Check if the sensitivityLabels is empty.
    // This is a special case because the sensitivityLabels is a treeview and cannot be validated using the normal form validation.
    // Skipped when the sensitivity-label feature is disabled, since the picker is hidden and no label is required.
    if (this.#sensitivityLabelService.enabled()
      && (this.agentForm.get('sensitivityLabels')?.value === null || this.agentForm.get('sensitivityLabels')?.value.length === 0)) {
      this.agentForm.get('sensitivityLabels')?.setErrors({ sensitivityLabelInvalid: true });
      this.agentForm.get('sensitivityLabels')?.markAsTouched();
    }

    return this.agentForm.valid;
  }

    /**
   * Method to update the checkbox state from the parent component
   */
  updateCheckedKeys(checkedKeys: string[]): void {
    this.checkedKeys.set(checkedKeys);
    this.agentForm.get('sensitivityLabels')?.setValue(checkedKeys);
  }

  /**
   * Updates the agent builder service with the current values from the agent form.
   * Maps selected sensitivity label GUIDs to their full label objects before updating.
   */
  updateAgent(): void {
    // Map checkedKeys (GUID strings) to full label objects
    const selectedLabels = this.checkedKeys()
      .map(id => this.sensitivityLabelData.find(label => label.id === id))
      // At runtime — !!label removes any undefined entries that resulted from a GUID not being found in sensitivityLabelData.
      // For TypeScript — the type predicate (label): label is NonNullable<typeof label> tells the compiler that after this filter,
      // the array is guaranteed to contain no undefined values.
      .filter((label): label is NonNullable<typeof label> => !!label);

    this.#agentBuilderService.agent.update(prev => ({
      ...prev,
      details: {
        ...prev?.details,
        ...this.agentForm.value,
        sensitivityLabels: selectedLabels
      }
    }));
  }

  /**
   * Handler for the keywordsChanged event emitted by the KeywordsControllerComponent.
   * Called whenever the keywords control value changes in the child component.
   * Updates the agent state with the latest form values.
   *
   * This is needed when the user invalidates the control and clicks the save button.
   */
  onKeywordsChanged() {
    this.updateAgent();
  }
}
